import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showExperianModal, setShowExperianModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null); // 'experian', 'any'
  const [returnedFromExperian, setReturnedFromExperian] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a retry upload or return from Experian
  const isRetry = location.state?.isRetry || false;
  const isReturn = location.state?.isReturn || false;
  
  // Get state from navigation or localStorage
  const timeline = location.state?.timeline || localStorage.getItem('selectedTimeline') || 6;
  const goalScore = location.state?.goalScore || localStorage.getItem('selectedGoalScore') || 720;
  const monthlyBudget = location.state?.monthlyBudget || localStorage.getItem('monthlyBudget') || 300;
  const hasPaid = location.state?.paid || localStorage.getItem('hasPaid') === 'true';
  const betaAccess = location.state?.betaAccess || localStorage.getItem('betaCode');

  // Check if user returned from Experian (via localStorage flag)
  useEffect(() => {
    const experianFlag = localStorage.getItem('experianRedirect');
    if (experianFlag === 'true' || isReturn) {
      setReturnedFromExperian(true);
      setSelectedSource('experian');
      localStorage.removeItem('experianRedirect');
    }
  }, [isReturn]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      uploadFile(droppedFile);
    } else {
      alert('Please upload a PDF file');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      uploadFile(selectedFile);
    } else {
      alert('Please upload a PDF file');
    }
  };

  const handleGetExperian = () => {
    setShowExperianModal(true);
  };

  const handleContinueToExperian = () => {
    // Set flag so we know user is returning from Experian
    localStorage.setItem('experianRedirect', 'true');
    localStorage.setItem('reportSource', 'experian');
    // Open Experian in new tab
    window.open('https://www.experian.com/consumer-products/free-credit-report.html', '_blank');
    setShowExperianModal(false);
    setReturnedFromExperian(true);
    setSelectedSource('experian');
  };

  const handleUploadExperian = () => {
    setSelectedSource('experian');
    localStorage.setItem('reportSource', 'experian');
  };

  const handleUploadAny = () => {
    setSelectedSource('any');
    localStorage.setItem('reportSource', 'any');
  };

  const uploadFile = async (fileToUpload) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      
      // Include report source for backend tracking
      const reportSource = localStorage.getItem('reportSource') || selectedSource || 'unknown';
      formData.append('report_source', reportSource);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const sessionId = response.data.session_id;
      
      // Store ALL session data in localStorage before navigating
      localStorage.setItem('creditSessionId', sessionId);
      localStorage.setItem('selectedTimeline', timeline);
      localStorage.setItem('selectedGoalScore', goalScore);
      localStorage.setItem('monthlyBudget', monthlyBudget);
      if (betaAccess) {
        localStorage.setItem('betaCode', betaAccess);
      }
      
      console.log('Upload complete - navigating to processing with sessionId:', sessionId);
      
      // If user has paid or has beta access, process directly
      if (hasPaid || betaAccess) {
        navigate('/processing', { 
          state: { 
            sessionId, 
            timeline: parseInt(timeline),
            goalScore: parseInt(goalScore),
            monthlyBudget: parseInt(monthlyBudget),
            skipPayment: true,
            betaCode: betaAccess,
            reportSource
          } 
        });
      } else {
        navigate('/processing', { 
          state: { 
            sessionId, 
            timeline: parseInt(timeline),
            goalScore: parseInt(goalScore),
            monthlyBudget: parseInt(monthlyBudget),
            reportSource
          } 
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file. Please try again.');
      setUploading(false);
    }
  };

  // Show upload dropzone when source is selected or user returned from Experian
  const showUploadArea = selectedSource || returnedFromExperian;

  return (
    <div className="upload-container">
      {/* Experian Instruction Modal */}
      {showExperianModal && (
        <div className="modal-overlay" onClick={() => setShowExperianModal(false)}>
          <div className="modal-content experian-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowExperianModal(false)}>×</button>
            
            <div className="experian-modal-header">
              <div className="experian-logo-badge">
                <span className="experian-e">E</span>
              </div>
              <h2>Get Your Free Experian Report</h2>
              <p className="modal-time">Takes 2-3 minutes</p>
            </div>

            <div className="experian-steps">
              <div className="experian-step">
                <span className="step-number">1</span>
                <span>Click "Continue to Experian" below</span>
              </div>
              <div className="experian-step">
                <span className="step-number">2</span>
                <span>Sign up or log in (it's free)</span>
              </div>
              <div className="experian-step">
                <span className="step-number">3</span>
                <span>Download your credit report as PDF</span>
              </div>
              <div className="experian-step">
                <span className="step-number">4</span>
                <span>Come back here and upload it</span>
              </div>
            </div>

            <div className="experian-note">
              <span className="note-icon">💡</span>
              <p>This will open Experian in a new tab. <strong>Keep this page open</strong> so you can return easily.</p>
            </div>

            <button 
              className="experian-continue-btn"
              onClick={handleContinueToExperian}
              data-testid="continue-to-experian-btn"
            >
              Continue to Experian
              <span className="external-icon">↗</span>
            </button>
          </div>
        </div>
      )}

      <div className="upload-box">
        {(hasPaid || betaAccess) && (
          <div className="upload-success-badge">
            <span>✓</span>
            <span>{betaAccess ? 'Beta Access Activated' : 'Payment Confirmed'}</span>
          </div>
        )}
        
        {isRetry && (
          <div className="upload-warning-banner">
            <p className="warning-title">⚠️ Previous upload couldn't be fully read</p>
            <p className="warning-text">
              Please upload a clearer PDF. Make sure it's not password-protected and contains all account details.
            </p>
          </div>
        )}

        {/* Return from Experian State */}
        {returnedFromExperian && !showUploadArea && (
          <div className="return-banner">
            <span className="return-icon">✓</span>
            <p>Welcome back! Upload your Experian report below.</p>
          </div>
        )}
        
        <h1 className="upload-title" data-testid="upload-title">
          {returnedFromExperian ? 'Upload Your Experian Report' : 
           isRetry ? 'Re-upload Your Credit Report' : 
           'Get Your Credit Report'}
        </h1>
        
        {!showUploadArea ? (
          <>
            <p className="upload-subtitle">
              Choose how you'd like to proceed
            </p>

            {/* Three Options */}
            <div className="report-options">
              {/* Option 1: Get Free Experian (Primary) */}
              <div 
                className="report-option primary"
                onClick={handleGetExperian}
                data-testid="get-experian-option"
              >
                <div className="option-badge">RECOMMENDED</div>
                <div className="option-icon experian-icon">
                  <span className="experian-e">E</span>
                </div>
                <h3 className="option-title">Get Your Free Experian Report</h3>
                <p className="option-subtitle">Free • Includes credit score • Best accuracy</p>
                <button className="option-btn primary-btn">
                  Get Experian Report
                  <span className="btn-arrow">→</span>
                </button>
              </div>

              {/* Option 2: Upload Experian */}
              <div 
                className="report-option"
                onClick={handleUploadExperian}
                data-testid="upload-experian-option"
              >
                <div className="option-icon">
                  <span className="upload-icon-sm">📄</span>
                </div>
                <h3 className="option-title">Upload Experian Report</h3>
                <p className="option-subtitle">Already have it? Upload now</p>
                <button className="option-btn">
                  Upload Experian
                </button>
              </div>

              {/* Option 3: Upload Any Report */}
              <div 
                className="report-option"
                onClick={handleUploadAny}
                data-testid="upload-any-option"
              >
                <div className="option-icon">
                  <span className="upload-icon-sm">📋</span>
                </div>
                <h3 className="option-title">Upload Any Credit Report</h3>
                <p className="option-subtitle">Supports TransUnion, Equifax, IdentityIQ, and others</p>
                <button className="option-btn">
                  Upload Your Report
                </button>
              </div>
            </div>

            {/* Helper Text */}
            <p className="helper-text">
              For the most accurate results, we recommend using a free Experian report. Other formats may have limited or inconsistent data.
            </p>
          </>
        ) : (
          <>
            {/* Upload Area */}
            <p className="upload-subtitle">
              {selectedSource === 'experian' || returnedFromExperian
                ? "Drop your Experian PDF here or click to browse"
                : "Drop your credit report PDF here or click to browse"}
            </p>

            {selectedSource === 'any' && (
              <div className="any-report-warning">
                <span className="warning-icon">⚠️</span>
                <p>Non-Experian reports may have limited or inconsistent data. For best results, use Experian.</p>
              </div>
            )}

            <div
              data-testid="dropzone"
              className={`dropzone ${isDragging ? 'active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <div className="upload-icon-large">📄</div>
              <div className="dropzone-text">
                {file ? file.name : 'Drag & drop your PDF here'}
              </div>
              <div className="dropzone-hint">or click to select file</div>
            </div>

            <input
              id="file-input"
              data-testid="file-input"
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {uploading && (
              <div className="uploading-indicator">
                <div className="upload-spinner"></div>
                <span>Uploading and analyzing your report...</span>
              </div>
            )}

            {/* Back button to choose different option */}
            <button 
              className="back-to-options-btn"
              onClick={() => {
                setSelectedSource(null);
                setReturnedFromExperian(false);
              }}
            >
              ← Choose a different option
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
